import { useCallback } from "react";
import type { Encargo, EncargoStatus, Promotion, RestockItem } from "@/types";
import type { StoreCtx } from "@/store/appTypes";
import {
  removeEncargo,
  removePromotion,
  removeRestockItem,
  saveEncargo,
  saveNotification,
  savePromotion,
  saveRestockItem,
  updateEncargoStatus,
} from "@/store/api";

// Promociones, pedidos por encargo y lista de reposición.
export const useContentActions = (ctx: StoreCtx) => {
  const { setData, run } = ctx;

  const addPromotion = useCallback(
    (promo: Promotion) =>
      run(async () => {
        const saved = await savePromotion(promo);
        await saveNotification({
          audience: "asesor",
          title: "Nueva promoción disponible",
          message: `${saved.title} · -${saved.discountPct}%`,
          kind: "promo",
          date: saved.createdAt || new Date().toISOString(),
          read: false,
          link: "/asesor",
        });
        setData((p) => ({ ...p, promotions: [saved, ...p.promotions] }));
      }),
    [run, setData]
  );

  const deletePromotion = useCallback(
    (id: string) =>
      run(async () => {
        await removePromotion(id);
        setData((p) => ({ ...p, promotions: p.promotions.filter((x) => x.id !== id) }));
      }),
    [run, setData]
  );

  const addEncargo = useCallback(
    (e: Encargo) =>
      run(async () => {
        const saved = await saveEncargo(e);
        setData((p) => ({ ...p, encargos: [saved, ...p.encargos] }));
      }),
    [run, setData]
  );

  const setEncargoStatus = useCallback(
    (id: string, status: EncargoStatus) =>
      run(async () => {
        const saved = await updateEncargoStatus(id, status);
        setData((p) => ({
          ...p,
          encargos: p.encargos.map((x) => (x.id === id ? saved : x)),
        }));
      }),
    [run, setData]
  );

  const deleteEncargo = useCallback(
    (id: string) =>
      run(async () => {
        await removeEncargo(id);
        setData((p) => ({ ...p, encargos: p.encargos.filter((x) => x.id !== id) }));
      }),
    [run, setData]
  );

  const addRestockItem = useCallback(
    (i: RestockItem) =>
      run(async () => {
        const saved = await saveRestockItem(i);
        setData((p) => ({ ...p, restockItems: [saved, ...p.restockItems] }));
      }),
    [run, setData]
  );

  const deleteRestockItem = useCallback(
    (id: string) =>
      run(async () => {
        await removeRestockItem(id);
        setData((p) => ({ ...p, restockItems: p.restockItems.filter((x) => x.id !== id) }));
      }),
    [run, setData]
  );

  return {
    addPromotion,
    deletePromotion,
    addEncargo,
    setEncargoStatus,
    deleteEncargo,
    addRestockItem,
    deleteRestockItem,
  };
};